import './imagePromise.scss';
import React, { useEffect, useState } from 'react';
import IconPresenter from 'hooks/IconPresenter';
import { IImagePromise } from 'types/interfaces';
import { ClipLoader } from 'react-spinners';
import Image from 'components/commons/image/Image';
import SvgIcon from 'components/commons/icons/SvgIcon';
import { ReactComponent as Ic_no_image } from 'resources/icons/Ic_no_image.svg';

const IMAGE_STORAGE_KEY = 'image_cache'; // Clave para el objeto en localStorage

// Función para validar si el localStorage está lleno
export function isLocalStorageFull() {
  const testKey = 'test_storage_full';
  try {
    localStorage.setItem(testKey, new Array(300 * 1024).join('a')); // 1 MB de datos
    localStorage.removeItem(testKey); // Limpia el dato de prueba
    return false; // No está lleno
  } catch (e) {
    return true; // Está lleno
  }
}

function ImagePromise(props: IImagePromise) {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const iconPresenter = IconPresenter();

  // Función para obtener el objeto de imágenes desde localStorage
  const getImageObject = () => {
    const cachedImages = localStorage.getItem(IMAGE_STORAGE_KEY);
    return cachedImages ? JSON.parse(cachedImages) : {};
  };

  // Función para buscar una imagen por imageId en el objeto
  const findImageById = (imageId: string | number) => {
    const imageObject = getImageObject();
    return imageObject[imageId] || null; // Devuelve la imagen o null si no existe
  };

  // Función para guardar una imagen en el objeto
  const saveImageToObject = (imageId: string | number, source: string) => {
    try {
      if(isLocalStorageFull()) return;
      const imageObject = getImageObject();
      imageObject[Number(imageId)] = source; // Agregar o actualizar la imagen
      localStorage.setItem(IMAGE_STORAGE_KEY, JSON.stringify(imageObject)); // Guardar en localStorage
    } catch {
      console.log('No se pudo guardar la imagen en la caché');
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(false);

    // Buscar la imagen en el objeto de localStorage
    const cachedImage = findImageById(Number(props.imageId));
    if (cachedImage) {
      setSrc(cachedImage);
      setLoading(false);
      return;
    }

    // Si no está en el objeto, cargarla desde el servidor
    const getImg = async () => {
      try {
        const icono = await iconPresenter.getImage(props.imageId);
        setSrc(icono);
        props.saveCache && saveImageToObject(props.imageId, icono); // Guardar en el objeto
      } catch (err) {
        console.log(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    getImg();
  }, [props.imageId]);

  return (
    <div className='pic-promisse-wrapper' onClick={() => props.onClick?.()}>
      {loading && (
        <div className='spinner-pic-promisse'>
          <ClipLoader color={'#869898'} speedMultiplier={0.7} size={60} />
        </div>
      )}
      {error && <div className='no-image'><SvgIcon Icon={Ic_no_image} ariaLabel='no-image'/></div>}
      {src ? (
        <Image
          className={props.className}
          style={props.style}
          src={src}
          ariaLabel={props.alt ?? 'SRC_' + props.imageId}
          alt={props.alt ?? 'SRC_' + props.imageId}
          loading='lazy'
        />
      ) : null}
    </div>
  );
}

export default React.memo(ImagePromise);
